import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { usePageTitle } from "@/hooks/usePageTitle";
import { buyerListService } from "@/services/buyerListService";
import { BuyerList } from "@/types/buyerSearch";
import { ArrowLeft, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SellerRestrictedPage from "@/components/seller/SellerRestrictedPage";

const EditBuyerListPage = () => {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [list, setList] = useState<BuyerList | null>(null);
  const [listName, setListName] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  usePageTitle("Edit Buyer List");

  useEffect(() => {
    if (!listId) return;
    
    const fetchListDetails = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await buyerListService.getListDetails(listId);

        if (error) {
          toast({
            title: "Error",
            description: error,
            variant: "destructive"
          });
          navigate("/seller/buyer-lists");
          return;
        }

        if (data) {
          setList(data);
          setListName(data.list_name);
          setDescription(data.description || "");
        }
      } catch (error) {
        console.error('Error fetching list details:', error);
        toast({
          title: "Error",
          description: "Failed to load list details",
          variant: "destructive"
        });
        navigate("/seller/buyer-lists");
      } finally {
        setIsLoading(false);
      }
    };

    fetchListDetails();
  }, [listId, navigate, toast]);

  const handleSave = async () => {
    if (!listId || !listName.trim()) return;
    
    setIsSaving(true);
    try {
      const success = await buyerListService.updateList(listId, {
        list_name: listName.trim(),
        description: description.trim() || null
      });

      if (success) {
        toast({
          title: "Success",
          description: "Buyer list updated successfully"
        });
        navigate(`/seller/buyer-lists/${listId}`);
      }
    } catch (error) {
      console.error('Error updating list:', error);
      toast({
        title: "Error",
        description: "Failed to update buyer list",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/seller/buyer-lists/${listId}`);
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading list details...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="py-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-muted-foreground">List not found</p>
              <Button 
                onClick={() => navigate("/seller/buyer-lists")} 
                className="mt-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Lists
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <SellerRestrictedPage featureName="edit buyer list">
      <div className="py-6">
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/seller/buyer-lists/${listId}`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <CardTitle>Edit Buyer List</CardTitle>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="listName">List Name *</Label>
              <Input
                id="listName"
                placeholder="e.g., Cash Buyers - Florida"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description for this list..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSave}
              disabled={!listName.trim() || isSaving}
              className="flex-1"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
      </div>
    </SellerRestrictedPage>
  );
};

export default EditBuyerListPage;